import { Copy, Pencil, Repeat, Trash2, UserCheck, UserPlus } from "lucide-react";
import type { ScheduleApplication } from "@models/Schedule";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getConsultantName } from "./ApplicantList";
import { REPEAT_PRESETS, ShiftRepeatPreset } from "./weekSchedule.utils";

interface Props {
	/** Pointer position (viewport) the menu opens at */
	position: { x: number; y: number };
	label: string;
	/** Set when the shift cannot be changed/removed, shown as a hint in the menu */
	lockedReason?: string;
	/** Copies can still be made of a locked (e.g. booked) shift */
	canCopy: boolean;
	/** Copies are added as drafts to send later, instead of being saved at once */
	copiesAsDrafts?: boolean;
	/** Shift is unmanned and the job still takes bookings */
	canStaff: boolean;
	pendingApplications: ScheduleApplication[];
	onChangeTimes: () => void;
	onDuplicate: () => void;
	onRepeat: (preset: ShiftRepeatPreset) => void;
	onRemove: () => void;
	/** Opens the "Bjud in konsulter" drawer */
	onInvite: () => void;
	onBook: (application: ScheduleApplication) => void;
	onClose: () => void;
}

/**
 * Right-click menu for a shift. Same approach as the dashboard's shift planner:
 * a dropdown menu rendered open, anchored to an invisible element at the pointer.
 */
const ShiftContextMenu = ({
	position,
	label,
	lockedReason,
	canCopy,
	copiesAsDrafts = false,
	canStaff,
	pendingApplications,
	onChangeTimes,
	onDuplicate,
	onRepeat,
	onRemove,
	onInvite,
	onBook,
	onClose,
}: Props) => (
	<DropdownMenu open onOpenChange={(open) => !open && onClose()}>
		<DropdownMenuTrigger asChild>
			<span className="pointer-events-none fixed h-px w-px" style={{ left: position.x, top: position.y }} />
		</DropdownMenuTrigger>
		<DropdownMenuContent align="start" sideOffset={2} className="w-64">
			<DropdownMenuLabel className="capitalize">{label}</DropdownMenuLabel>
			{canStaff && (
				<>
					<DropdownMenuSeparator />
					<DropdownMenuSub>
						<DropdownMenuSubTrigger disabled={!pendingApplications.length}>
							<UserCheck />
							Boka konsult
							{!pendingApplications.length && <span className="ml-auto text-xs text-gray-400">Inga ansökningar</span>}
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className="w-64">
							{pendingApplications.map((application) => (
								<DropdownMenuItem key={application.id} onSelect={() => onBook(application)}>
									<span className="min-w-0">
										<span className="block truncate">{getConsultantName(application.consultant)}</span>
										<span className="block truncate text-xs text-gray-500">
											{[
												application.consultant.profile?.address?.city,
												application.consultant.hasCustomerExperience && "Tidigare anlitad",
											]
												.filter(Boolean)
												.join(" · ")}
										</span>
									</span>
								</DropdownMenuItem>
							))}
						</DropdownMenuSubContent>
					</DropdownMenuSub>
					<DropdownMenuItem onSelect={onInvite}>
						<UserPlus className="!text-orange-500" />
						Önska konsult
					</DropdownMenuItem>
				</>
			)}
			<DropdownMenuSeparator />
			<DropdownMenuItem disabled={!!lockedReason} onSelect={onChangeTimes}>
				<Pencil />
				Ändra tider
			</DropdownMenuItem>
			<DropdownMenuItem disabled={!canCopy} onSelect={onDuplicate}>
				<Copy />
				Duplicera
				{copiesAsDrafts && <span className="ml-auto text-xs text-gray-400">som nytt pass</span>}
			</DropdownMenuItem>
			<DropdownMenuSub>
				<DropdownMenuSubTrigger disabled={!canCopy}>
					<Repeat />
					Repetera
				</DropdownMenuSubTrigger>
				<DropdownMenuSubContent>
					{REPEAT_PRESETS.map((preset) => (
						<DropdownMenuItem key={preset.key} onSelect={() => onRepeat(preset.key)}>
							{preset.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuSubContent>
			</DropdownMenuSub>
			<DropdownMenuSeparator />
			<DropdownMenuItem
				disabled={!!lockedReason}
				onSelect={onRemove}
				className="text-[#EB5757] focus:bg-[#FFEBEB] [&_svg]:text-[#EB5757]"
			>
				<Trash2 />
				Ta bort pass
			</DropdownMenuItem>
			{lockedReason && <div className="px-2 pb-1 pt-1.5 text-xs text-gray-500">{lockedReason}</div>}
		</DropdownMenuContent>
	</DropdownMenu>
);

export default ShiftContextMenu;
